/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useCallback, useState } from 'react';
import { utils, hooks, unstable } from '@broadleaf/admin-components';
import cx from 'classnames';
import { get, has } from 'lodash';
import PropTypes from 'prop-types';

import {
  ActionPropShape,
  ComponentPropShape
} from 'app/common/constants/CommonProps';

import { LocalStateShape } from '../../../../../AuroraOrderView/prop-types';
import { useContextParams } from '../../hooks';
import { queueFetch } from '../../../../../AuroraOrderView/state/actions';

const { useEventCallback } = hooks;
const { RequestUtils } = utils;
const { MetadataUtils } = unstable;
const { getAttribute, getEndpointByType } = MetadataUtils;

const { request } = RequestUtils;

const AuroraOrderApiRetry = ({
  actionDefinition,
  fulfillment,
  fulfillmentsState,
  orderDispatch,
  orderMetadata,
  orderState
}) => {
  const [disabled, toggleDisabled] = useState(false);
  const contextParams = useContextParams(
    orderMetadata,
    orderState,
    fulfillment
  );

  const submitEndpoint = getEndpointByType(
    actionDefinition,
    'AURORA_ORDER_API_RETRY'
  );

  const handleSuccess = useEventCallback(() => {
    // trigger a refresh of the fulfillments collection
    fulfillmentsState.handleRead();
  }, [fulfillmentsState.handleRead]);

  const doRetry = useCallback(async () => {
    try {
      toggleDisabled(true);
      const response = await request(
        {
          method: 'patch',
          ...submitEndpoint
        },
        contextParams
      );

      handleSuccess(response);
      orderDispatch(queueFetch());
    } catch (error) {
      console.error('Error re-sending order to aurora api:', error);
      toggleDisabled(false);
    } finally {
      toggleDisabled(false);
    }
  }, [contextParams, handleSuccess, orderDispatch, submitEndpoint]);

  const order = get(orderState, 'data', {});
  if (!has(order, 'auroraApiErrorMessage')) {
    return null;
  }

  return (
    <RetryAction
      actionDefinition={actionDefinition}
      doRetry={doRetry}
      disabled={disabled}
    />
  );
};

AuroraOrderApiRetry.propTypes = {
  actionDefinition: ActionPropShape,
  fulfillment: PropTypes.object,
  fulfillmentsState: PropTypes.object,
  itemsMetadata: ComponentPropShape,
  orderDispatch: PropTypes.func,
  orderMetadata: ComponentPropShape,
  orderState: LocalStateShape
};

const RetryAction = ({ actionDefinition, doRetry, disabled }) => {
  const submitLabel = getAttribute(
    actionDefinition,
    'submitLabel',
    actionDefinition.label
  );

  return (
    <>
      <RetryButton doRetry={doRetry} disabled={disabled}>
        {submitLabel}
      </RetryButton>
    </>
  );
};

const RetryButton = ({ children, disabled = false, doRetry }) => {
  return (
    <button
      className={cx(
        'resubmit-dms-button focus:tw-shadow-outline tw-rounded tw-border tw-border-gray-200 tw-px-4 tw-py-4 tw-text-sm tw-font-semibold tw-text-yellow-700 tw-shadow focus:tw-outline-none md:tw-mb-3 md:tw-py-2 lg:tw-w-full',
        {
          'tw-bg-white hover:tw-bg-yellow-100 focus:tw-bg-yellow-100 active:tw-bg-yellow-200':
            !disabled
        },
        {
          'tw-cursor-not-allowed tw-bg-gray-300': disabled
        }
      )}
      disabled={disabled}
      onClick={doRetry}
      type="button"
    >
      {children}
    </button>
  );
};

export default AuroraOrderApiRetry;
export { AuroraOrderApiRetry };
